/**
 * Launch Hardening — Mobile Match Mode + Graceful Degradation Layer.
 *
 * Detects device memory, connection quality, viewport and (optionally) FPS
 * health to derive a "perf profile": low | mid | high. Profile drives
 * glow intensity, animation density, overlay caps, ticker frequency.
 *
 * Pure JS singleton, no network. Exposes:
 *   - initMobileMatchMode(): one-shot bootstrap (safe to call once).
 *   - useMobileMatchMode(): React hook returning current profile.
 *   - getProfile(): sync getter.
 */
import { useSyncExternalStore } from "react";
import { setMaxStrong } from "./priorityVisualQueue";

export type PerfProfile = "low" | "mid" | "high";

export interface MobileMatchModeState {
  profile: PerfProfile;
  fps: number;
  isMobile: boolean;
  saveData: boolean;
  reducedMotion: boolean;
  /** 0..1 multiplier for glow/box-shadow intensity. */
  glowScale: number;
  /** 0..1 multiplier for animation duration/density. */
  animationScale: number;
  /** Cap on simultaneous strong overlays. */
  overlayCap: number;
  /** Recommended ticker tick interval (ms). */
  tickerIntervalMs: number;
}

const DEFAULT: MobileMatchModeState = {
  profile: "high",
  fps: 60,
  isMobile: false,
  saveData: false,
  reducedMotion: false,
  glowScale: 1,
  animationScale: 1,
  overlayCap: 2,
  tickerIntervalMs: 3000,
};

let state: MobileMatchModeState = DEFAULT;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => state;

function profileFor(opts: { mem: number; downlink: number; saveData: boolean; isMobile: boolean; fps: number; reducedMotion: boolean }): PerfProfile {
  if (opts.reducedMotion || opts.saveData) return "low";
  if (opts.mem <= 2 || opts.downlink <= 1.2 || opts.fps < 35) return "low";
  if (opts.mem <= 4 || opts.downlink <= 3 || opts.fps < 50 || opts.isMobile) return "mid";
  return "high";
}

function deriveFromProfile(profile: PerfProfile, base: Partial<MobileMatchModeState>): MobileMatchModeState {
  switch (profile) {
    case "low":
      return { ...DEFAULT, ...base, profile, glowScale: 0.45, animationScale: 0.55, overlayCap: 1, tickerIntervalMs: 6000 };
    case "mid":
      return { ...DEFAULT, ...base, profile, glowScale: 0.75, animationScale: 0.8, overlayCap: 2, tickerIntervalMs: 4000 };
    case "high":
    default:
      return { ...DEFAULT, ...base, profile, glowScale: 1, animationScale: 1, overlayCap: 2, tickerIntervalMs: 3000 };
  }
}

function applyCssVars(s: MobileMatchModeState) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.style.setProperty("--mmm-glow-scale", String(s.glowScale));
  el.style.setProperty("--mmm-anim-scale", String(s.animationScale));
  el.dataset.perfProfile = s.profile;
}

function applyState(next: MobileMatchModeState) {
  state = next;
  setMaxStrong(next.overlayCap);
  applyCssVars(next);
  emit();
}

let bootstrapped = false;

export function initMobileMatchMode() {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;

  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { downlink?: number; saveData?: boolean; effectiveType?: string; addEventListener?: (e: string, cb: () => void) => void } };
  const mem = typeof nav.deviceMemory === "number" ? nav.deviceMemory : 4;
  const downlink = nav.connection?.downlink ?? 10;
  const saveData = !!nav.connection?.saveData;
  const isMobile = window.matchMedia?.("(max-width: 768px)").matches ?? false;
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  const profile = profileFor({ mem, downlink, saveData, isMobile, fps: 60, reducedMotion });
  applyState(deriveFromProfile(profile, { isMobile, saveData, reducedMotion, fps: 60 }));

  // Reflect connection / motion changes.
  const recompute = () => {
    const newDownlink = nav.connection?.downlink ?? downlink;
    const newSaveData = !!nav.connection?.saveData;
    const newReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const next = profileFor({ mem, downlink: newDownlink, saveData: newSaveData, isMobile, fps: state.fps, reducedMotion: newReduced });
    if (next !== state.profile || newReduced !== state.reducedMotion || newSaveData !== state.saveData) {
      applyState(deriveFromProfile(next, { isMobile, saveData: newSaveData, reducedMotion: newReduced, fps: state.fps }));
    }
  };
  nav.connection?.addEventListener?.("change", recompute);
  window.matchMedia?.("(prefers-reduced-motion: reduce)").addEventListener?.("change", recompute);

  // ----- Graceful Degradation: lightweight FPS sampler. -----
  // Samples once per 4s using requestAnimationFrame over ~30 frames.
  let stopped = false;
  const sample = () => {
    if (stopped) return;
    let frames = 0;
    const start = performance.now();
    const tick = () => {
      frames += 1;
      if (frames < 30) {
        requestAnimationFrame(tick);
      } else {
        const elapsed = performance.now() - start;
        const fps = Math.round((frames * 1000) / elapsed);
        const newProfile = profileFor({
          mem,
          downlink: nav.connection?.downlink ?? downlink,
          saveData: !!nav.connection?.saveData,
          isMobile,
          fps,
          reducedMotion,
        });
        if (Math.abs(fps - state.fps) > 4 || newProfile !== state.profile) {
          applyState(deriveFromProfile(newProfile, { isMobile, saveData: !!nav.connection?.saveData, reducedMotion, fps }));
        }
        setTimeout(sample, 4000);
      }
    };
    requestAnimationFrame(tick);
  };
  // Defer first sample so initial render isn't measured.
  setTimeout(sample, 2500);

  // Stop sampling while hidden to conserve battery.
  document.addEventListener("visibilitychange", () => {
    stopped = document.hidden;
    if (!stopped) setTimeout(sample, 1500);
  });
}

export function getProfile(): MobileMatchModeState {
  return state;
}

export function useMobileMatchMode(): MobileMatchModeState {
  return useSyncExternalStore(subscribe, snap, snap);
}
